import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { FamilyMember, useFamilyMemberStore } from '../../store/useFamilyMemberStore';
import { Button, Label } from 'flowbite-react';
import { HiArrowLeft } from 'react-icons/hi';
import dayjs from 'dayjs';

export default function FamilyMemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getFamilyMember = useFamilyMemberStore((state) => state.getFamilyMember);
  const loading = useFamilyMemberStore((state) => state.loading);

  const [member, setMember] = useState<FamilyMember | null>(null);

  const loadMember = useCallback(async () => {
    if (!id) return;
    const memberData = await getFamilyMember(id);
    if (memberData) {
      setMember(memberData);
    }
  }, [id, getFamilyMember]);

  useEffect(() => {
    if (id) {
      loadMember();
    }
  }, [id, loadMember]);

  if (!member) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            {loading ? 'Loading...' : 'Family member not found'}
          </p>
        </div>
      </div>
    );
  }

  const familyName = typeof member.family === 'object' && member.family ? member.family.familyName : 'N/A';
  const familyId = typeof member.family === 'object' && member.family ? member.family._id : typeof member.family === 'string' ? member.family : '';
  const familyUnit =
    typeof member.family === 'object' && member.family && member.family.unit
      ? typeof member.family.unit === 'object'
        ? member.family.unit
        : null
      : null;
  const unitName = familyUnit ? familyUnit.name : null;
  const unitNumber = familyUnit ? familyUnit.unitNumber : null;
  const unitId = familyUnit ? familyUnit._id : null;
  const motherName = typeof member.mother === 'object' && member.mother ? member.mother.name : null;
  const fatherName = typeof member.father === 'object' && member.father ? member.father.name : null;

  return (
    <>
      <PageMeta title="Family Member Details" description="View family member information" />
      <PageBreadcrumb pageTitle="Family Member Details" />
      <div className="space-y-6">
        <ComponentCard title="Family Member Information">
          <div className="space-y-6">
            {/* Header with Back Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {member.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Family: {familyName}
                </p>
              </div>
              <Button
                onClick={() => navigate(`/family-members`)}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiArrowLeft className="mr-2" /> Back to Family Members
                </span>
              </Button>
            </div>

            {/* Family Member Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label value="Name" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {member.name}
                </p>
              </div>
              <div>
                <Label value="Family" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {familyId ? (
                    <button
                      onClick={() => navigate(`/families/${familyId}`)}
                      className="text-[#B8860B] hover:underline"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    >
                      {familyName}
                    </button>
                  ) : (
                    familyName
                  )}
                </p>
              </div>
              {unitName && unitNumber && (
                <div>
                  <Label value="Unit" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {unitId ? (
                      <button
                        onClick={() => navigate(`/unit/${unitId}`)}
                        className="text-[#B8860B] hover:underline"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        {unitName} ({unitNumber})
                      </button>
                    ) : (
                      `${unitName} (${unitNumber})`
                    )}
                  </p>
                </div>
              )}
              {member.dob && (
                <div>
                  <Label value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.dob).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.education && (
                <div>
                  <Label value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {member.education}
                  </p>
                </div>
              )}
              {member.job && (
                <div>
                  <Label value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {member.job}
                  </p>
                </div>
              )}
              <div>
                <Label value="Marital Status" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <span
                    className={`px-2 py-1 rounded text-sm font-semibold ${
                      member.married
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {member.married ? 'Married' : 'Single'}
                  </span>
                </p>
              </div>
              {motherName && (
                <div>
                  <Label value="Mother" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {motherName}
                  </p>
                </div>
              )}
              {fatherName && (
                <div>
                  <Label value="Father" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {fatherName}
                  </p>
                </div>
              )}
              {member.baptismDate && (
                <div>
                  <Label value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.baptismDate).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.firstCommunionDate && (
                <div>
                  <Label value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.firstCommunionDate).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.confirmationDate && (
                <div>
                  <Label value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.confirmationDate).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.marriedDate && (
                <div>
                  <Label value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.marriedDate).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.deathDate && (
                <div>
                  <Label value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-red-600 dark:text-red-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(member.deathDate).format('DD MMM YYYY')}
                  </p>
                </div>
              )}
              {member.contactNumbers && member.contactNumbers.length > 0 && (
                <div className="col-span-2">
                  <Label value="Contact Numbers" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <div className="flex flex-wrap gap-2 mt-1">
                    {member.contactNumbers.map((contact, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        {contact}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {dayjs(member.createdAt).format('DD MMM YYYY, hh:mm A')}
                </p>
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>
    </>
  );
}
